/**
 * shell/flowRows.ts —— **中栏流行还原**（★C-Ⅷ）：闭集载体行 → 12 类节点行。
 *
 * 由来（逐条可追）：后端投影把读面行放在**九键闭集载体**上（`ViewAssembly.fact`，
 * `normalize_fact` 闭集；同 `sidebar.session_row_source` 的会话行口径），前端据 **声明**
 * （`shell.decl.yaml` `main.flow_row_source`）还原成渲染层要的节点行形态
 * `{key, kind, createdAt, state}`（`renderer/flowNodes.tsx::toFlowNode` 的入参形）。
 *
 * 纪律：
 *  · **只读**（零取数、零网络）；口径**只从声明来**（禁在此硬编码第二份对应表）；
 *  · 数据值不在 `kind_map` 词表内 ⇒ 返回 `null`（**不算节点行**）——目录数据／骨架行
 *    绝不冒充会话内容（`views/ViewDocument.tsx` 的「无节点行 ⇒ 空态」纪律由此承接）；
 *  · 缺值保留缺席（不补造）；默认值只有两处且是**读面的事实**（见 §settled）。
 */
import type { FlowRowSourceDecl } from "./shellDecl";

export interface FlowRow {
  /** 供 `ProjectionInput.flowRows`（`Record<string, unknown>`）直用 */
  [extra: string]: unknown;
  key: string;
  kind: string;
  createdAt?: string;
  /** 节点 state（渲染件按节点类取用：`message.user.text`／`assistant.status+blocks` …） */
  state: Record<string, unknown>;
}

function pick(row: Record<string, string>, candidates: readonly string[]): string | undefined {
  for (const name of candidates) {
    const value = row[name];
    if (typeof value === "string" && value !== "") return value;
  }
  return undefined;
}

/** 单行 → 节点行；**数据值不在 `kind_map` 词表内 ⇒ `null`**（不冒充节点）。 */
export function flowRowOf(
  row: Record<string, string>,
  decl: FlowRowSourceDecl,
  index = 0,
  /** ★F3d：`id → 名` 映射（如附件 `file_id → filename`；缺 ⇒ 条目保持原串，不造名） */
  names?: Record<string, string>,
): FlowRow | null {
  const dataValue = pick(row, [decl.kind_from]);
  const kind = dataValue ? decl.kind_map[dataValue] : undefined;
  if (!kind) return null;
  const state: Record<string, unknown> = {};
  const listFields = new Set(decl.list_fields ?? []);   // ★F3c：声明为列表型的字段 ⇒ 拆成数组
  for (const [name, candidates] of Object.entries(decl.fields)) {
    const value = pick(row, candidates);
    if (value === undefined) continue;
    state[name] = listFields.has(name)
      // 列表型字段：条目若命中 `names`（如附件 id → 文件名）⇒ 发 `{file_id, filename}`（渲染件已支持）
      ? value.split(/\s+/).filter(Boolean)
          .map((item) => (names && names[item] ? { file_id: item, filename: names[item] } : item))
      : value;
  }
  // 读面行**皆已落库件**（流式中的消息不在库里）⇒ 声明的定稿类节点按定稿态呈现。
  if (decl.settled.includes(kind)) {
    state.status = "settled";
    const text = typeof state.text === "string" ? state.text : "";
    state.blocks = text ? [text] : [];
  }
  const createdAt = typeof state.createdAt === "string" ? state.createdAt : undefined;
  return { key: pick(row, decl.key) ?? "row-" + index, kind, createdAt, state };
}

/** 行集 → 节点行集（保序；`null` 的整行丢弃——词表外的行不是会话内容）。 */
export function flowRowsFrom(
  rows: ReadonlyArray<Record<string, string>>,
  decl: FlowRowSourceDecl,
  names?: Record<string, string>,
): FlowRow[] {
  const out: FlowRow[] = [];
  rows.forEach((row, index) => {
    const node = flowRowOf(row, decl, index, names);
    if (node) out.push(node);
  });
  return out;
}
